// apps/console/src/services/api/robot-positions-store.ts
//
// React-free pub/sub for the fleet's CURRENT robot positions (Robot Fleet
// Dashboard, sub-project 3). Mirrors robot-alerts-store.ts: a module-level
// listener Set plus a module-level snapshot array of the held positions. Fed by
// the live channel (useLiveChannel's location branch + the kind:'location'
// snapshot) and read by the fleet_map element. Positions are tenant-global
// CURRENT STATE (the same regardless of which dashboard views them), so they
// live in a module-global store — only *selection* is per-dashboard. Unlike
// alerts there is no open/closed status: every robot's latest position is held,
// upserted by robotId (newest wins).

export interface RobotPosition {
  robotId: string
  name?: string
  lng: number
  lat: number
  heading?: number
  battery?: number
  recordedAt?: string
  seq?: number
}

type Listener = (positions: RobotPosition[]) => void

const listeners = new Set<Listener>()
let positions: RobotPosition[] = []

function notify(): void {
  // Hand each subscriber a fresh array reference so React state setters see a
  // changed identity and re-render.
  const snapshot = positions.slice()
  listeners.forEach(l => l(snapshot))
}

/**
 * True when `incoming` is a strictly OLDER fix than `held`, judged by the device's own `recordedAt`.
 *
 * A GPS tracker buffers records while it has no signal and flushes them oldest-first when it
 * reconnects, so a backlog can arrive AFTER a newer live fix. Without this the marker jumps back to
 * where the device was an hour ago. Only a comparison both sides can actually make counts: an absent
 * or unparseable time on either side keeps the old newest-wins behaviour, which is what every robot
 * and helmet (whose frames are always current) relies on. Equal times are not older.
 */
function isOlderFix(incoming: RobotPosition, held: RobotPosition | undefined): boolean {
  if (!held?.recordedAt || !incoming.recordedAt) return false

  const incomingMs = Date.parse(incoming.recordedAt)
  const heldMs = Date.parse(held.recordedAt)

  if (!Number.isFinite(incomingMs) || !Number.isFinite(heldMs)) return false

  return incomingMs < heldMs
}

/** Upsert a single live position (newest wins, kept newest-first). Called from
 *  useLiveChannel's location branch on each live robot:location frame. An
 *  incoming fix recorded strictly before the held one is ignored (no notify). */
export function emitRobotPosition(p: RobotPosition): void {
  const held = positions.find(existing => existing.robotId === p.robotId)

  if (isOlderFix(p, held)) return

  positions = [p, ...positions.filter(existing => existing.robotId !== p.robotId)]
  notify()
}

/** REPLACE the held list from a location snapshot. Called from useLiveChannel
 *  when a kind:'location' snapshot arrives (the broker fleet snapshot, S2). */
export function seedRobotPositions(rows: RobotPosition[]): void {
  positions = rows.slice()
  notify()
}

/** Subscribe; immediately receives the current position list. Returns an
 *  unsubscribe fn. */
export function subscribeRobotPositions(listener: Listener): () => void {
  listeners.add(listener)
  listener(positions.slice())

  return () => {
    listeners.delete(listener)
  }
}

/** Test-only reset of the module state. */
export function __resetRobotPositionsForTests(): void {
  listeners.clear()
  positions = []
}
